import React from "react";
import { useLazyInternationalPhone } from "utils/lazy-libs";

// Type definitions for the library
type CountryData = any;
type ParsedCountry = any;

// countries at the top
const TOP_ISO2 = [
  "am", // Armenia
  "ru", // Russia
  "us", // USA
  "ua", // Ukraine
  "kz", // Kazakhstan
  "ge", // Georgia
  "by", // Belarus
  "uz", // Uzbekistan
  "de", // Germany
  "fr", // France
  "gb", // United Kingdom
  "il", // Israel
  "tr", // Turkey
  "cn", // China
  "in", // India
];

function getKzAreaCodes() {
  return [
    ...Array.from(
      { length: 100 },
      (_, i) => `76${i.toString().padStart(2, "0")}`
    ),
    ...Array.from(
      { length: 100 },
      (_, i) => `77${i.toString().padStart(2, "0")}`
    ),
  ];
}

function patchCountries(
  list: CountryData[],
  parseCountry: (country: CountryData) => ParsedCountry,
  buildCountryData: (country: ParsedCountry) => CountryData
): CountryData[] {
  const patched = list.map((c) => {
    const p = parseCountry(c);

    if (p.iso2 === "kz") {
      p.priority = 1;
      p.areaCodes = getKzAreaCodes();
    }
    if (p.iso2 === "ru") {
      p.priority = 0;
    }
    return buildCountryData(p);
  });

  // Sort: Top countries first, then rest alphabetically
  patched.sort((a, b) => {
    const aParsed = parseCountry(a);
    const bParsed = parseCountry(b);

    const aTop = TOP_ISO2.indexOf(aParsed.iso2);
    const bTop = TOP_ISO2.indexOf(bParsed.iso2);

    if (aTop !== -1 && bTop !== -1) return aTop - bTop;
    if (aTop !== -1) return -1;
    if (bTop !== -1) return 1;
    return aParsed.name.localeCompare(bParsed.name);
  });

  return patched; 
}

export const useCountriesFixed = () => {
  const { internationalPhone, isLoading } = useLazyInternationalPhone();
  
  const countriesFixed = React.useMemo(() => {
    if (!internationalPhone) return [];
    
    const { defaultCountries, parseCountry, buildCountryData } = internationalPhone;
    return patchCountries(defaultCountries, parseCountry, buildCountryData);
  }, [internationalPhone]);

  return { countriesFixed, isLoading };
};
